import CollapsibleView from "@components/molecules/CollapsibleView";
import { useTypographyStore } from "@redux/slices/typographySlice";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FlatList, Platform } from "react-native";
import { useTheme } from "styled-components/native";
import styled from "styled-components/native";
import { ProductDetailProps } from "../types";
import { useGetPdpSectionLayout } from "../../../hooks/useGetSectionLayout";
import { CustomtOption, PdpSections } from "@utils/customization";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { getProductProperty } from "@scenes/findMatch/Utils/ProductDetailList/utils";
import { dataMapValidateValue } from "./utils";
import { ProductCategory } from "@utils/constants";
import { useFilteredOptions } from "../../../hooks/useFilteredOptions";
import TwoColumnLabelView from "@components/molecules/TwoColumnLabelView";
import { SearchFilterTypeEnum } from "@components/atoms/Filters/utils";
import { Diamond } from "@src/types/DiamondDetail";
import { useUserAnalytics } from "../../../hooks/useAnalytics";
import {
  EventName,
  EventType,
  getAnalyticsProductData,
} from "@utils/analytics";

const PairDetailExpand = ({
  itemDetail,
  title,
  isPdpScreen,
  productType,
  isFindAMatch,
}: ProductDetailProps) => {
  const [customOptions, setCustomOptions] = useState<CustomtOption[]>([]);
  const userInfo = useUserInfo();
  const theme = useTheme();
  const { t } = useTranslation();
  const typography = useTypographyStore();
  const { trackButtonEvent } = useUserAnalytics() ?? {};
  const priceMode = useMemo(
    () => userInfo?.user?.price_mode,
    [userInfo?.user?.price_mode]
  );
  const stoneType = SearchFilterTypeEnum.PAIRS;

  const sectionLayout = useGetPdpSectionLayout(
    isFindAMatch
      ? PdpSections.MATCH_PAIR_PRODUCT_DETAILS
      : PdpSections.PRODUCT_DETAILS,
    productType,
    stoneType
  );

  const getCustomArray = () => {
    const isDataMappedValuePresent = (dataProperty: string) => {
      const dataMapedValue = getProductProperty(
        itemDetail?.[dataProperty as keyof typeof itemDetail],
        ""
      );
      return dataMapedValue !== "-";
    };

    const filteredSideData = customOptions.filter((item) =>
      item.name.includes("side_stone")
    );
    const filteredCenterData = customOptions.filter((item) =>
      item.name.includes("center_")
    );
    let isSideStoneValuePresent = false;
    let isCenterStoneValuePresent = false;

    if (productType === ProductCategory.JEWELRY) {
      filteredSideData.forEach((item) => {
        if (isDataMappedValuePresent(item.name)) {
          isSideStoneValuePresent = true;
        }
      });

      filteredCenterData.forEach((item) => {
        if (isDataMappedValuePresent(item.name)) {
          isCenterStoneValuePresent = true;
        }
      });
    }

    let updatedCustomOptions = customOptions;

    if (productType === ProductCategory.JEWELRY && !isSideStoneValuePresent) {
      updatedCustomOptions = updatedCustomOptions.filter(
        (item) => !item.name.includes("side_stone")
      );
    }

    if (productType === ProductCategory.JEWELRY && !isCenterStoneValuePresent) {
      updatedCustomOptions = updatedCustomOptions.filter(
        (item) => !/center_|centerstone/i.test(item.name)
      );
    }

    if (
      productType === ProductCategory.JEWELRY &&
      (!isCenterStoneValuePresent || !isSideStoneValuePresent)
    ) {
      updatedCustomOptions = updatedCustomOptions.filter(
        (item) => !item.type.includes("space")
      );
    }

    return updatedCustomOptions.map((option, index) => ({
      id: index + 1,
      title: t(option?.label),
      description:
        option?.type === "space" || option?.type === "title"
          ? ""
          : itemDetail?.children?.map((childDetail: Diamond) =>
              dataMapValidateValue(
                option?.name,
                productType,
                childDetail,
                isPdpScreen
              )
            ),
      icon: option?.icon.data,
      showNumber:
        productType === ProductCategory.JEWELRY &&
        dataMapValidateValue(option?.name, productType, itemDetail) === "-" &&
        option?.type !== "space" &&
        option?.type !== "title"
          ? false
          : true,
      price_modes: option.price_modes,
      order: option.order,
      isTitle: option?.type === "title" ? true : false,
      isSingleRow: option?.isSingleRow,
    }));
  };

  const filteredOptions = useFilteredOptions(theme, priceMode, sectionLayout);

  useEffect(() => {
    setCustomOptions(filteredOptions);
  }, [filteredOptions]);

  const keyExtractor = (item: any, index: number) => {
    if (item?.id) {
      const sectionIdentifier =
        (item as any).sectionTitle || (item as any).type || "";
      const uniqueId = (item as any).fav_id || item.id || index;
      return `${sectionIdentifier}-${uniqueId}-${index}`;
    }
    return index + "-item";
  };

  const formattedData = getCustomArray();

  const handlePhoneClick = useCallback(() => {
    trackButtonEvent?.(
      EventName.CONTACT_VENDOR_THROUGH_CALL,
      EventType.CONTACT_VENDOR_THROUGH_CALL,
      {
        ...getAnalyticsProductData(itemDetail),
        button_click_type: "link",
        button_description: "contact_vendor_through_call",
        button_name: "contact_vendor_through_call",
      }
    );
  }, [itemDetail, trackButtonEvent]);

  const ItemCard = ({ item }: any) => (
    <>
      {item?.showNumber === undefined || item?.showNumber === true ? (
        <CustomView style={{ width: "100%" }}>
          <TwoColumnLabelView
            labelText={item.title}
            valueLText={
              item.description &&
              Array.isArray(item.description) &&
              item.description.length
                ? item.description[0]
                : "-"
            }
            valueRText={
              item.description &&
              Array.isArray(item.description) &&
              item.description.length > 1
                ? item.description[1]
                : "-"
            }
            ellipsize={false}
            itemLeftTextTheme={
              item.isTitle && item.isTitle === true
                ? theme["core-05"]
                : theme["core-06"]
            }
            keyTypographyStyle={
              item.isTitle && item.isTitle === true
                ? typography["Body-M"]
                : typography["Body-R"]
            }
            valueTypographyStyle={typography["Body-M"]}
            clickableHandle={item?.clickable}
            keyWidth={85}
            valueWidthPercent={theme.isMobile ? 75 : 80}
            marginRight={theme.isMobile ? 4 : 10}
            isFixWidth
            onPhoneClick={item?.clickable ? handlePhoneClick : undefined}
          />
        </CustomView>
      ) : null}
    </>
  );

  return (
    <>
      <CollapsibleView
        initExpanded={true}
        title={title}
        arrowStyling={{ color: theme["core-04"] }}
        applyShadow
      >
        <Divider />
        <Container>
          <FlatList
            data={formattedData}
            renderItem={({ item }) => <ItemCard item={item} />}
            keyExtractor={keyExtractor}
            scrollEnabled={Platform.OS === "web" ? true : false}
          />
        </Container>
      </CollapsibleView>
    </>
  );
};

const Container = styled.View`
  padding: ${({ theme }) => (theme.isMobile ? "8px 0px" : "8px 16px")};
`;

const CustomView = styled.View`
  justify-content: flex-start;
  margin-vertical: 4px;
`;

const Divider = styled.View`
  height: 1px;
  flex-direction: row;
  background-color: ${({ theme }) => theme["core-03"]};
  width: 100%;
  max-width: 100%;
`;

export default PairDetailExpand;
